import React, { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import { Analytics } from '@vercel/analytics/react';

const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // St Augustine, Florida specific data
  const PHONE_NUMBER = "407-326-0479";
  const COMPANY_NAME = "St Augustine Emergency Pest Control";
  const LICENSE_NUMBER = "";
  
  // Service areas in St Johns County
  const SERVICE_AREAS = [
    "St Augustine", "St Augustine Beach", "Ponte Vedra Beach", 
    "Palm Coast", "Hastings", "Elkton", "Crescent City"
  ];

  // Handle scroll for header optimization
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Optimized menu toggle
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  // Analytics tracking for calls (placeholder)
  const trackCall = useCallback((source) => {
    // gtag('event', 'phone_call', { source: source });
    console.log('Call tracked from:', source);
  }, []);

  // EmailJS Configuration - Complete setup ready to use!
  const EMAIL_CONFIG = {
    SERVICE_ID: 'service_hf0rpwv',        // ✅ Your Gmail Service
    TEMPLATE_ID: 'template_fswiq7h',      // ✅ Your Business Notification Template
    CUSTOMER_TEMPLATE_ID: 'template_t2sws2a', // ← Replace with your Customer Template ID
    PUBLIC_KEY: 'h0cvbpkDutypYyHlB'       // ✅ Your Public Key
  };

  // Initialize EmailJS
  useEffect(() => {
    console.log('🔧 Initializing EmailJS with public key:', EMAIL_CONFIG.PUBLIC_KEY);
    emailjs.init(EMAIL_CONFIG.PUBLIC_KEY);
  }, [EMAIL_CONFIG.PUBLIC_KEY]);

  // Form handling with React Hook Form
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm();

  // Handle form submission with EmailJS
  const onSubmit = async (data) => {
    try {
      // Prepare email data
      const emailData = {
        to_email: 'contact@wm-leads.com',  // 📧 ADD YOUR BUSINESS EMAIL HERE
        to_name: 'St Augustine Pest Control',
        from_name: data.name,
        customer_name: data.name,
        customer_phone: data.phone,
        customer_email: data.email,
        email: data.email,  // 📧 For EmailJS template Reply To field
        customer_address: data.address,
        pest_type: data.pestType,
        service_area: data.serviceArea || 'Not specified',
        urgency_level: data.urgency,
        additional_details: data.details || 'No additional details provided',
        submission_time: new Date().toLocaleString(),
        urgency_emoji: data.urgency === 'emergency' ? '🚨 EMERGENCY' : 
                      data.urgency === 'urgent' ? '⚡ URGENT' : 
                      data.urgency === 'soon' ? '📅 SOON' : '🛡️ PREVENTIVE'
      };

      console.log('Sending lead data via EmailJS:', emailData);

      // Send business notification email
      const result = await emailjs.send(
        EMAIL_CONFIG.SERVICE_ID,
        EMAIL_CONFIG.TEMPLATE_ID,
        emailData,
        EMAIL_CONFIG.PUBLIC_KEY
      );

      // Send customer confirmation email
      if (EMAIL_CONFIG.CUSTOMER_TEMPLATE_ID !== 'template_XXXXX') {
        await emailjs.send(
          EMAIL_CONFIG.SERVICE_ID,
          EMAIL_CONFIG.CUSTOMER_TEMPLATE_ID,
          emailData,
          EMAIL_CONFIG.PUBLIC_KEY
        );
        console.log('Customer confirmation sent to:', data.email);
      }

      console.log('EmailJS Success:', result);
      
      // Success message based on urgency
      const urgencyMessage = data.urgency === 'emergency' 
        ? '🚨 EMERGENCY Request Sent! We\'ll call you within 5 minutes!\n\n📧 Confirmation email sent to ' + data.email
        : data.urgency === 'urgent'
        ? '⚡ URGENT Request Sent! We\'ll contact you within 2 hours!\n\n📧 Confirmation email sent to ' + data.email
        : '📧 Request Sent Successfully! We\'ll contact you soon.\n\nConfirmation email sent to ' + data.email;
      
      alert(urgencyMessage);
      reset(); // Clear the form
      trackCall('form-submission-success');
      
    } catch (error) {
      console.error('❌ EmailJS Error Details:', {
        error: error,
        message: error.message,
        status: error.status,
        text: error.text
      });
      
      // More specific error handling
      let errorMessage = '❌ There was an issue sending your request.\n\n';
      
      if (error.status === 400) {
        errorMessage += '🔧 Configuration Error: Please check EmailJS setup.\n\n';
      } else if (error.status === 403) {
        errorMessage += '🔑 Authentication Error: Invalid EmailJS credentials.\n\n';
      } else if (error.text && error.text.includes('template')) {
        errorMessage += '📧 Template Error: Email template configuration issue.\n\n';
      } else {
        errorMessage += '🌐 Network Error: Please check your internet connection.\n\n';
      }
      
      errorMessage += `For immediate emergency service, please call: ${PHONE_NUMBER}\n\nOr email us directly at: contact@wm-leads.com\n\nWe apologize for the technical difficulty and will resolve this quickly.`;
      
      alert(errorMessage);
      
      trackCall('form-submission-error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags - Critical for Google Rankings */}
      <head>
        <title>Best Pest Control St Augustine FL - #1 Emergency Exterminator 24/7</title>
        <meta name="description" content="Top-rated pest control St Augustine FL. Emergency exterminator 24/7. Licensed, insured, 500+ 5-star reviews. Call 407-326-0479 for same-day service in St Johns County." />
        <meta name="keywords" content="pest control st augustine, exterminator st augustine fl, emergency pest control, palmetto bug removal, termite treatment, st johns county pest control" />
        <meta name="geo.placename" content="St Augustine, Florida" />
        <meta name="geo.region" content="FL" />
        <meta name="geo.position" content="29.9012;-81.3124" />
        <meta property="og:title" content="Best Pest Control St Augustine FL - Emergency 24/7 Service" />
        <meta property="og:description" content="St Augustine's top-rated emergency pest control service. Licensed FL exterminators available 24/7. Call 407-326-0479 for same-day service." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://staugustinepest.com" />
        <link rel="canonical" href="https://staugustinepest.com" />
      </head>

      {/* Schema.org Structured Data for Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "St Augustine Emergency Pest Control", 
          "description": "Professional emergency pest control services in St Augustine, FL",
          "telephone": "407-326-0479",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Your Street Address",
            "addressLocality": "St Augustine",
            "addressRegion": "FL", 
            "postalCode": "32084"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "29.9012",
            "longitude": "-81.3124"
          },
          "url": "https://staugustinepest.com",
          "areaServed": ["St Augustine", "St Johns County", "Ponte Vedra Beach"],
          "serviceType": "Pest Control Services",
          "priceRange": "$150-400",
          "aggregateRating": {
            "@type": "AggregateRating", 
            "ratingValue": "4.9",
            "reviewCount": "500"
          },
          "openingHours": "Mo,Tu,We,Th,Fr,Sa,Su 00:00-23:59"
        })}
      </script>

      {/* Emergency Top Banner - Critical above fold */}
      <div className="emergency-banner text-white py-2 px-4 text-center sticky top-0 z-50">
        <p className="text-sm md:text-base font-bold">
          🚨 ST AUGUSTINE PEST EMERGENCY? Call Now: 
          <a 
            href={`tel:${PHONE_NUMBER}`}
            onClick={() => trackCall('emergency-banner')}
            className="phone-number text-lg ml-2 hover:text-yellow-200"
          >
            {PHONE_NUMBER}
          </a> - 24/7 Service!
        </p>
      </div>

      {/* Header - Performance optimized */}
      <header className={`transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-lg' : 'bg-white shadow-lg'
      } sticky top-8 z-40`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo - Local branding */}
            <div className="flex items-center">
              <img 
                src="/favicon-32x32.png" 
                alt={COMPANY_NAME}
                className="h-12 w-auto mr-3"
                onError={(e) => {
                  // Fallback to text if logo doesn't load
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <div className="text-2xl font-bold text-gray-800" style={{display: 'none'}}>
                <span className="text-primary-600">🏠</span> {COMPANY_NAME}
              </div>
              <div className="flex flex-col">
                <div className="text-lg md:text-xl font-bold text-gray-800">
                  {COMPANY_NAME}
                </div>
                <div className="hidden md:block text-sm text-gray-600">
                  Serving St Augustine & St Johns County
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <a href="#services" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">Services</a>
              <a href="#areas" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">Service Areas</a>
              <a href="#about" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">About Us</a>
              <a href="#faq" className="text-gray-700 hover:text-primary-600 font-medium transition-colors">FAQ</a>
            </nav>

            {/* Phone CTA - Conversion optimized */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-600">St Augustine Emergency Line</p>
                <a 
                  href={`tel:${PHONE_NUMBER}`}
                  onClick={() => trackCall('header')}
                  className="phone-number text-2xl font-bold text-primary-600 hover:text-primary-700 transition-colors"
                >
                  {PHONE_NUMBER}
                </a>
              </div>
              <a
                href={`tel:${PHONE_NUMBER}`}
                onClick={() => trackCall('header-cta')}
                className="cta-button bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-bold transition-all"
              >
                CALL NOW
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t animate-fade-in">
              <div className="flex flex-col space-y-4">
                <a href="#services" className="text-gray-700 font-medium py-2" onClick={toggleMenu}>Services</a>
                <a href="#areas" className="text-gray-700 font-medium py-2" onClick={toggleMenu}>Service Areas</a>
                <a href="#about" className="text-gray-700 font-medium py-2" onClick={toggleMenu}>About Us</a>
                <a href="#faq" className="text-gray-700 font-medium py-2" onClick={toggleMenu}>FAQ</a>
                <div className="pt-4 border-t">
                  <a 
                    href={`tel:${PHONE_NUMBER}`}
                    onClick={() => trackCall('mobile-menu')}
                    className="phone-number text-xl font-bold text-primary-600 block"
                  >
                    {PHONE_NUMBER}
                  </a>
                  <p className="text-sm text-gray-600">24/7 Emergency Service in St Augustine</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - Local SEO Optimized */}
      <section className="bg-gradient-to-br from-primary-50 via-florida-50 to-staugustine-50 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {/* Hero Title - SEO optimized */}
              <h1 className="hero-title text-4xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                <span className="text-primary-600">24/7 Emergency Pest Control in St Augustine, FL</span><br />
                <span className="text-gray-800">Call Now – Same-Day Service Available</span>
              </h1>
              
              <div className="local-highlight p-4 rounded-lg mb-6">
                <p className="text-lg font-semibold text-staugustine-800 mb-2">
                  🏆 St Augustine's Top-Rated Pest Control Company
                </p>
                <p className="text-staugustine-700">
                  Serving historic homes and businesses in St Johns County since 2010. Same-day emergency service available.
                </p>
              </div>

              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                <strong>Emergency pest control St Augustine FL</strong> - Licensed Florida exterminators available <strong>24/7</strong> for same-day service. 
                Specializing in <strong>palmetto bugs, fire ants, termites, and rodent control</strong> throughout St Johns County.
              </p>
              


              {/* CTA Buttons - Conversion optimized */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <a
                  href={`tel:${PHONE_NUMBER}`}
                  onClick={() => trackCall('hero-primary')}
                  className="cta-button bg-primary-600 hover:bg-primary-700 text-white text-xl font-bold py-4 px-8 rounded-lg text-center shadow-cta hover:shadow-cta-hover"
                >
                  📞 CALL NOW FOR FREE INSPECTION
                </a>
                <a
                  href="#contact"
                  className="cta-button bg-white hover:bg-gray-50 text-primary-600 border-2 border-primary-600 text-lg font-bold py-4 px-8 rounded-lg text-center"
                >
                  Get Free St Augustine Quote
                </a>
              </div>
            </div>

            {/* Hero Image - Professional pest control in St Augustine */}
            <div className="relative h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://plus.unsplash.com/premium_photo-1661810432776-82ac9fc31ade?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MzN8fHBlc3QlMjBjb250cm9sfGVufDB8fDB8fHww"
                alt="Professional exterminator treating historic St Augustine home for pest control"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <div className="text-white p-8 w-full">
                  <div className="bg-black/50 backdrop-blur-sm rounded-lg p-6">
                    <h3 className="text-xl font-bold mb-2">St Augustine's #1 Pest Control</h3>
                    <p className="text-sm opacity-90">Licensed • Emergency Service • Historic Home Specialists</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form - Conversion Optimized */}
      <section id="contact" className="py-16 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Get Emergency Pest Control in St Augustine Now!
            </h2>
            <p className="text-xl text-gray-300">
              Fill out the form below or call <span className="phone-number text-primary-400">{PHONE_NUMBER}</span> for immediate assistance
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="bg-white rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Request Emergency Service</h3>
              <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    placeholder="Your full name"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('name', { 
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Name must be at least 2 characters' }
                    })}
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    placeholder="(904) 123-4567"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('phone', { 
                      required: 'Phone number is required',
                      pattern: { 
                        value: /^[+]?[1-9]?[\d\s\-()]{10,}$/,
                        message: 'Please enter a valid phone number' 
                      }
                    })}
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    placeholder="your.email@example.com"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('email', { 
                      required: 'Email address is required',
                      pattern: { 
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address' 
                      }
                    })}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    📧 We'll send you a confirmation and follow-up details
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">St Augustine Address *</label>
                  <input
                    type="text"
                    placeholder="Service address in St Augustine area"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.address ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('address', { 
                      required: 'Service address is required',
                      minLength: { value: 5, message: 'Please enter a complete address' }
                    })}
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pest Problem *</label>
                  <select 
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.pestType ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('pestType', { required: 'Please select a pest type' })}
                  >
                    <option value="">Select pest type</option>
                    <option value="palmetto-bugs">Palmetto Bugs/Cockroaches</option>
                    <option value="fire-ants">Fire Ants</option>
                    <option value="spiders">Spiders (Black Widow/Brown Recluse)</option>
                    <option value="rodents">Mice/Rats/Roof Rats</option>
                    <option value="termites">Termites</option>
                    <option value="bedbugs">Bed Bugs</option>
                    <option value="wasps">Wasps/Yellow Jackets</option>
                    <option value="fleas">Fleas/Ticks</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.pestType && <p className="text-red-500 text-sm mt-1">{errors.pestType.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Area</label>
                  <select 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    {...register('serviceArea')}
                  >
                    <option value="">Select your area</option>
                    {SERVICE_AREAS.map((area, index) => (
                      <option key={index} value={area.toLowerCase().replace(' ', '-')}>{area}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Urgency Level *</label>
                  <select 
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                      errors.urgency ? 'border-red-500' : 'border-gray-300'
                    }`}
                    {...register('urgency', { required: 'Please select urgency level' })}
                  >
                    <option value="">How urgent is this?</option>
                    <option value="emergency">🚨 EMERGENCY - Need service today in St Augustine</option>
                    <option value="urgent">⚡ URGENT - Within 24 hours</option>
                    <option value="soon">📅 Soon - Within this week</option>
                    <option value="preventive">🛡️ Preventive - Schedule when convenient</option>
                  </select>
                  {errors.urgency && <p className="text-red-500 text-sm mt-1">{errors.urgency.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Additional Details</label>
                  <textarea
                    rows="3"
                    placeholder="Describe the problem, areas affected, severity... (e.g., 'Saw palmetto bugs in kitchen', 'Fire ant mounds in yard')"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    {...register('details')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full font-bold py-3 px-6 rounded-lg cta-button transition-all ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-primary-600 hover:bg-primary-700'
                  } text-white`}
                >
                  {isSubmitting ? '🔄 Submitting...' : '🚨 REQUEST EMERGENCY SERVICE IN ST AUGUSTINE'}
                </button>

                <p className="text-sm text-gray-500 text-center">
                  For immediate service, call <strong>{PHONE_NUMBER}</strong>
                </p>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <div className="bg-primary-600 text-white rounded-lg p-6 text-center">
                <h3 className="text-2xl font-bold mb-4">St Augustine Emergency Hotline</h3>
                <a 
                  href={`tel:${PHONE_NUMBER}`}
                  onClick={() => trackCall('contact-sidebar')}
                  className="phone-number text-3xl font-bold block mb-2 hover:text-primary-100 transition-colors"
                >
                  {PHONE_NUMBER}
                </a>
                <p className="text-primary-100 mb-4">Available 24/7 for pest emergencies</p>
                <div className="animate-pulse inline-block bg-primary-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  Local St Augustine Operators Standing By
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">📍 Service Coverage</h3>
                <div className="space-y-2 text-gray-700 text-sm">
                  {SERVICE_AREAS.map((area, index) => (
                    <p key={index}>✓ {area}</p>
                  ))}
                  <p className="text-primary-600 font-medium mt-4">
                    📞 Call to confirm service in your specific area
                  </p>
                </div>
              </div>

             {/* <div className="bg-white rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">🕐 Business Hours</h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Regular Service:</strong> Mon-Fri 8AM-6PM</p>
                  <p><strong>Saturday:</strong> 9AM-4PM</p>
                  <p><strong>Emergency Service:</strong> <span className="text-primary-600 font-bold">24/7</span></p>
                  <p className="text-sm text-gray-500 mt-3 italic">
                    Serving St Johns County 
                  </p>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section - Critical for Local SEO */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What St Augustine Customers Say About Our Pest Control
            </h2>
            <div className="flex justify-center items-center space-x-2 mb-4">
              <div className="text-yellow-400 text-2xl">★★★★★</div>
              <span className="text-xl font-bold text-gray-800">4.9/5</span>
              <span className="text-gray-600">(500+ Reviews)</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <img 
                  src="https://plus.unsplash.com/premium_photo-1690407617542-2f210cf20d7e?q=80&w=687&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                  alt="Maria Rodriguez - St Augustine customer review"
                  className="w-12 h-12 rounded-full mr-3 object-cover object-center"
                />
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Best pest control company in St Augustine! They eliminated our palmetto bug problem in our historic downtown home. Professional, fast, and reasonably priced."
              </p>
              <div className="text-sm text-gray-600">
                <strong>Maria Rodriguez</strong> - Historic District
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <img 
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&auto=format&q=80" 
                  alt="John Thompson - St Augustine Beach customer review"
                  className="w-12 h-12 rounded-full mr-3 object-cover"
                />
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Emergency fire ant treatment at our St Augustine Beach rental property. They came out same day and solved the problem completely. Highly recommend!"
              </p>
              <div className="text-sm text-gray-600">
                <strong>John Thompson</strong> - St Augustine Beach
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-center mb-3">
                <img 
                  src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&auto=format&q=80" 
                  alt="Sarah Chen - Anastasia Island customer review"
                  className="w-12 h-12 rounded-full mr-3 object-cover"
                />
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Top-notch termite inspection and treatment. They understand historic St Augustine construction and protected our coquina walls. 5 stars!"
              </p>
              <div className="text-sm text-gray-600">
                <strong>Sarah Chen</strong> - Anastasia Island
              </div>
            </div>
          </div>

          <div className="text-center">
            
            <a
              href={`tel:${PHONE_NUMBER}`}
              onClick={() => trackCall('reviews-cta')}
              className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-8 rounded-lg"
            >
              📞 CALL NOW FOR FREE INSPECTION - {PHONE_NUMBER}
            </a>
          </div>
        </div>
      </section>

      {/* Emergency Services - Florida Specific */}
      <section id="services" className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              <span className="text-primary-600">St Augustine</span> Emergency Pest Control Services
            </h2>
            <p className="text-xl text-gray-600 mb-4">
              Florida's warm climate creates year-round pest problems - don't let them take over!
            </p>
            <div className="inline-block bg-warning-50 border border-warning-200 rounded-lg p-4">
              <p className="text-warning-800 font-semibold">
                🌴 Florida Climate Alert: Peak pest season is year-round. Act fast!
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { 
                pest: "🪳 Palmetto Bugs", 
                urgency: "Health Hazard!", 
                color: "primary",
                description: "Florida's giant cockroaches carry diseases"
              },
              { 
                pest: "🐜 Fire Ants", 
                urgency: "Painful Stings!", 
                color: "staugustine", 
                description: "Aggressive Florida fire ants"
              },
              { 
                pest: "🕷️ Widow Spiders", 
                urgency: "Venomous Bites!", 
                color: "gray", 
                description: "Black widows common in FL"
              },
              { 
                pest: "🐭 Roof Rats", 
                urgency: "Disease Risk!", 
                color: "florida", 
                description: "Florida's climbing rats"
              }
            ].map((service, index) => (
              <div key={index} className={`trust-badge border-2 border-${service.color}-200 p-6 text-center hover:border-${service.color}-300 transition-all relative overflow-hidden`}>
                
                <div className="text-4xl mb-3">{service.pest.split(' ')[0]}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.pest.split(' ').slice(1).join(' ')}</h3>
                <div className={`bg-${service.color}-100 text-${service.color}-800 px-3 py-1 rounded-full text-sm font-bold mb-2`}>
                  ⚠️ {service.urgency}
                </div>
                <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                <a
                  href={`tel:${PHONE_NUMBER}`}
                  onClick={() => trackCall(`service-${service.pest.toLowerCase()}`)}
                  className={`bg-${service.color}-600 hover:bg-${service.color}-700 text-white px-4 py-2 rounded-lg font-bold text-sm w-full block transition-all`}
                >
                  Emergency Treatment
                </a>
              </div>
            ))}
          </div>

          {/* Additional Florida Pests */}
          <div className="bg-gray-50 rounded-lg p-8">
            <div className="text-center mb-6">
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Other Florida Pests We Eliminate in St Augustine:
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center mb-8">
              {[
                'Subterranean Termites', 'Carpenter Ants', 'Yellow Jackets', 'Fleas & Ticks', 
                'Mosquitoes', 'Silverfish', 'Carpet Beetles', 'Pantry Pests'
              ].map((pest, index) => (
                <div key={index} className="bg-white rounded-lg py-3 px-2 shadow-sm hover:shadow-md transition-shadow">
                  <span className="font-medium text-gray-800 text-sm">{pest}</span>
                </div>
              ))}
            </div>
            
            {/* Free Inspection CTA */}
            <div className="text-center">
              <div className="bg-primary-600 text-white rounded-lg p-6 inline-block">
                <h4 className="text-2xl font-bold mb-2">🔍 FREE Pest Inspection</h4>
                <p className="text-primary-100 mb-4">Professional assessment of your St Augustine property - No obligation!</p>
                <a
                  href={`tel:${PHONE_NUMBER}`}
                  onClick={() => trackCall('services-free-inspection')}
                  className="bg-white text-primary-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg text-lg transition-all inline-block"
                >
                  📞 CALL NOW FOR FREE INSPECTION
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              About St Augustine Emergency Pest Control
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your trusted local pest control experts, proudly serving St Augustine and surrounding communities
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our St Augustine Story</h3>
              <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
                <p>
                  St Augustine Emergency Pest Control has been protecting historic homes and businesses throughout 
                  St Johns County for over a decade.
                </p>
                <p>
                  We know that living in Florida's subtropical climate and historic architecture creates specific 
                  pest control needs. From the ancient coquina walls of downtown to the waterfront properties 
                  at St Augustine Beach, our team has the local expertise to handle any pest emergency.
                </p>
                <p>
                  Our team has grown into the area's most trusted emergency pest 
                  control service, but we've never forgotten our roots in this historic community.
                </p>
              </div>
            </div> 
        
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-6">
              <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎯</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Local Expertise</h4>
              <p className="text-gray-600">
                Born and raised in St Augustine, our team understands the specific pest challenges of historic Florida architecture and coastal living.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-success-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">⚡</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Emergency Response</h4>
              <p className="text-gray-600">
                Available 24/7 for pest emergencies. We know that some pest problems can't wait until business hours.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="bg-florida-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🌿</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Eco-Conscious</h4>
              <p className="text-gray-600">
                Protecting St Augustine's natural beauty with environmentally responsible pest control methods that are safe for families and pets.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Us?</h3>
              <p className="text-xl text-gray-600">
                More than just pest control - we're your neighbors protecting your most valuable investment
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">🏠 Historic Home Specialists</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Experience with coquina and historic materials
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Preservation-conscious treatment methods
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Understanding of old St Augustine construction
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-4">🌊 Coastal Environment Experts</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Humidity and moisture control strategies
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Salt-air resistant treatment options
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Coastal pest behavior expertise
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center mt-8">
              <a
                href={`tel:${PHONE_NUMBER}`}
                onClick={() => trackCall('about-cta')}
                className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 px-8 rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                📞 Call Our St Augustine Team: {PHONE_NUMBER}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas - Local SEO */}
      <section id="areas" className="py-16 px-4 bg-florida-gradient text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Serving St Johns County & Beyond</h2>
            <p className="text-xl text-blue-900">
              Fast emergency pest control throughout the greater St Augustine area
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-4">🏛️ Historic St Augustine</h3>
                <ul className="space-y-2 text-blue-900">
                  <li>Historic Downtown</li>
                  <li>Anastasia Island</li>
                  <li>Davis Shores</li>
                  <li>Lighthouse Area</li>
                </ul>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-4">🏖️ Coastal Communities</h3>
                <ul className="space-y-2 text-blue-900">
                  <li>St Augustine Beach</li>
                  <li>Ponte Vedra Beach</li>
                  <li>Vilano Beach</li>
                  <li>Summer Haven</li>
                </ul>
              </div>
            </div>
            
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-2xl font-bold mb-4">🌳 St Johns County</h3>
                <ul className="space-y-2 text-blue-900">
                  <li>Hastings</li>
                  <li>Elkton</li>
                  <li>Crescent City</li>
                  <li>Flagler Estates</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-blue-900 mb-4">Don't see your area listed? We may still serve you!</p>
            <a
              href={`tel:${PHONE_NUMBER}`}
              onClick={() => trackCall('service-area')}
              className="inline-block bg-white text-florida-600 font-bold py-3 px-8 rounded-lg hover:bg-blue-50 transition-all shadow-lg"
            >
              Call {PHONE_NUMBER} to Check Coverage
            </a>
          </div>
        </div>
      </section>

      {/* Free Inspection CTA Section - Strategic Placement */}
      <section className="py-16 px-4 bg-gradient-to-r from-primary-600 to-primary-700">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 md:p-12">
            <div className="text-6xl mb-6">🔍</div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              FREE Professional Pest Inspection
            </h2>
            <p className="text-xl text-primary-100 mb-6 leading-relaxed">
              Not sure what pests you're dealing with? Our expert technicians will inspect your St Augustine property 
              at <strong>no cost</strong> and provide a detailed assessment with treatment recommendations.
            </p>
            
            <div className="bg-white/20 rounded-lg p-4 mb-8 inline-block">
              <div className="text-lg font-bold mb-2">✅ What's Included in Your FREE Inspection:</div>
              <ul className="text-left text-primary-100 space-y-1">
                <li>• Complete interior and exterior property assessment</li>
                <li>• Pest identification and entry point detection</li>
                <li>• Written report with treatment recommendations</li>
                <li>• No-obligation estimate for treatment options</li>
              </ul>
            </div>

            <div className="space-y-4">
              <a
                href={`tel:${PHONE_NUMBER}`}
                onClick={() => trackCall('free-inspection-cta')}
                className="inline-block bg-white text-primary-600 hover:bg-gray-100 font-bold text-xl py-4 px-8 rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                📞 CALL NOW FOR FREE INSPECTION
              </a>
              <div className="text-primary-100">
                <div className="font-bold text-2xl">{PHONE_NUMBER}</div>
                <div className="text-sm">Available 24/7 - Same-day appointments available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section - Local Credibility */}
      <section className="bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why St Augustine Trusts Us for Pest Emergencies
            </h2>
            <div className="mb-8">
              <img 
                src="https://images.unsplash.com/photo-1749030415358-f533ad412767?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8cGVzdCUyMGNvbnRyb2wlMjBiYWR8ZW58MHx8MHx8fDA%3D"
                alt="Licensed pest control technicians with professional equipment in St Augustine"
                className="w-full max-w-4xl mx-auto h-32 object-cover rounded-lg shadow-md"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="trust-badge p-6">
                <div className="bg-success-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🇫🇱</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Florida Licensed & Certified</h3>
                <p className="text-gray-600">
                  FL License #{LICENSE_NUMBER}. Certified technicians protecting St Augustine homes and businesses.
                </p>
              </div>
            </div>

            <div className="text-center">
              <div className="trust-badge p-6">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⭐</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">500+ Five-Star Reviews</h3>
                <div className="text-yellow-500 text-xl mb-2">★★★★★</div>
                <p className="text-gray-600 italic">
                  "Saved our historic St Augustine home from termites! Professional and fast service." - Maria S.
                </p>
              </div>
            </div>

            <div className="text-center">
              <div className="trust-badge p-6">
                <div className="bg-staugustine-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🛡️</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">100% Satisfaction Guarantee</h3>
                <p className="text-gray-600">
                  Not satisfied? We return for free or refund your money. Backed by $1M liability insurance.
                </p>
              </div>
            </div>
          </div>

          {/* Local Business Credentials */}
          <div className="bg-white rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Proud Member of St Augustine Business Community</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
              <div>🏛️ St Augustine Chamber of Commerce</div>
              <div>🇫🇱 Florida Pest Management Association</div>
              <div>🛡️ Better Business Bureau A+</div>
              <div>⭐ Google Guaranteed Business</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Long-tail SEO Keywords */}
      <section id="faq" className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions - St Augustine Pest Control
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about pest control services in St Augustine, Florida
            </p>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                How much does pest control cost in St Augustine FL?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>Pest control costs in St Augustine typically range from $150-400</strong> for one-time treatments, 
                with monthly service plans starting at $50-80. Emergency services may have additional fees. 
                We provide free estimates and transparent pricing for all St Augustine residents.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What pests are most common in St Augustine Florida?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>St Augustine's coastal climate attracts palmetto bugs, fire ants, termites, spiders</strong>, mosquitoes, 
                and occasional rodents. Historic homes with coquina walls may have unique pest entry points. 
                Our local technicians specialize in Florida's year-round pest control needs.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Does St Augustine have palmetto bugs year-round?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>Yes, palmetto bugs (American cockroaches) are active year-round in St Augustine</strong> due to Florida's 
                warm, humid climate. They're most active in summer but never fully disappear. Professional treatment 
                every 60-90 days provides optimal control in St Johns County homes.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Do you offer same-day pest control service in St Augustine?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>Yes! We provide same-day emergency pest control throughout St Augustine</strong>, St Augustine Beach, 
                Ponte Vedra, and St Johns County. Call {PHONE_NUMBER} for immediate assistance with 
                urgent pest problems. Available 24/7 for true emergencies.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Are your pest treatments safe for historic St Augustine homes?
              </h3>
              <p className="text-gray-700 leading-relaxed">
                <strong>Absolutely. We specialize in historic St Augustine properties</strong> with coquina walls and vintage construction. 
                Our preservation-conscious methods protect your home's integrity while eliminating pests. 
                EPA-approved treatments safe for families, pets, and historic materials.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Still Have Questions About St Augustine Pest Control?
            </h3>
            <a
              href={`tel:${PHONE_NUMBER}`}
              onClick={() => trackCall('faq-cta')}
              className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 px-8 rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              📞 Call Now for Free Inspection: {PHONE_NUMBER}
            </a>
          </div>
        </div>
      </section>

      

      {/* Footer - Local SEO Enhanced */}
      <footer className="bg-gray-800 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4"> {COMPANY_NAME}</h3>
              <p className="text-gray-300 mb-4">
                Professional pest control services serving St Augustine and St Johns County Florida.
              </p>
             {/* <div className="text-gray-300 text-sm">
                <p>FL License #{LICENSE_NUMBER}</p>
                <p>BBB A+ Rating</p>
              </div>*/}
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">Emergency Contact</h3>
              <div className="space-y-2">
                <a 
                  href={`tel:${PHONE_NUMBER}`}
                  onClick={() => trackCall('footer')}
                  className="phone-number text-xl text-primary-400 block hover:text-primary-300 transition-colors"
                >
                  {PHONE_NUMBER}
                </a>
                <p className="text-gray-300">24/7 Emergency Service</p>
                
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">St Augustine Services</h3>
              <ul className="space-y-1 text-gray-300 text-sm">
                <li>Palmetto Bug Extermination</li>
                <li>Fire Ant Control</li>
                <li>Spider Removal</li>
                <li>Rodent Control</li>
                <li>Termite Treatment</li>
                <li>Bed Bug Elimination</li>
                <li>Wasp Nest Removal</li>
                <li>Flea & Tick Treatment</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">Service Areas</h3>
              <ul className="space-y-1 text-gray-300 text-sm">
                {SERVICE_AREAS.map((area, index) => (
                  <li key={index}>📍 {area}</li>
                ))}
              </ul>
             {/* <div className="mt-4">
                <h4 className="font-medium text-white mb-2">Follow Us</h4>
                <div className="flex space-x-4 text-sm">
                  <a href="https://facebook.com" className="text-gray-300 hover:text-white transition-colors">Facebook</a>
                  <a href="https://google.com/business" className="text-gray-300 hover:text-white transition-colors">Google</a>
                  <a href="https://yelp.com" className="text-gray-300 hover:text-white transition-colors">Yelp</a>
                </div>
              </div> */}
            </div>
          </div>

          {/* Final Emergency CTA */}
          <div className="border-t border-gray-700 pt-8 text-center">
            <div className="bg-primary-600 rounded-lg p-6 max-w-2xl mx-auto relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 opacity-20">
                <img 
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=80&h=80&fit=crop&auto=format&q=80"
                  alt="Emergency pest control"
                  className="w-full h-full object-cover rounded-bl-lg"
                />
              </div>
              <h3 className="text-2xl font-bold mb-2 relative z-10">Don't Let Pests Take Over Your St Augustine Property!</h3>
              <p className="mb-4 text-primary-100 relative z-10">Same-day emergency service available throughout St Johns County</p>
              <a
                href={`tel:${PHONE_NUMBER}`}
                onClick={() => trackCall('footer-final-cta')}
                className="inline-block bg-white text-primary-600 font-bold py-3 px-8 rounded-lg text-xl cta-button hover:bg-gray-100 relative z-10"
              >
                📞 Call {PHONE_NUMBER} Now!
              </a>
            </div>
            <div className="mt-6 text-sm text-gray-400">
              <p>© 2026 {COMPANY_NAME}. All rights reserved. | Serving St Augustine, FL and surrounding areas.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Call Button (Mobile) - Performance optimized */}
      <div className="fixed bottom-4 right-4 z-50 md:hidden no-print">
        <a
          href={`tel:${PHONE_NUMBER}`}
          onClick={() => trackCall('floating-mobile')}
          className="bg-primary-600 hover:bg-primary-700 text-white p-4 rounded-full shadow-lg animate-bounce-slow cta-button"
          aria-label="Call now for emergency pest control"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
        </a>
      </div>

      {/* Vercel Analytics */}
      <Analytics />
    </div>
  );
};

export default App;